import React, { useState, useEffect, useMemo } from 'react';
import { compose } from 'redux';

import PageLayout from 'layouts/PageLayout';
import Sidebar from 'scenes/Sidebar';
import FiltersAccordion from 'scenes/FiltersAccordion';
import FiltersWidget from 'scenes/FiltersAccordion/components/FiltersWidget';
import UpgradeCandidateAccount from 'scenes/Dialog/components/UpgradeCandidateAccount';
import UpgradeRecruiterAccount from 'scenes/Dialog/components/UpgradeRecruiterAccount';
import ListSearch from 'components/ListSearch';
import List from 'scenes/List';
import NetworkItem from 'scenes/List/components/NetworkItem';
import FiltersContentLoader from 'components/FiltersContentLoader';
import ListSearchContentLoader from 'components/ListSearchContentLoader';
import GridHistoryWithFilters from 'components/GridHistoryWithFilters';
import NoEventsMask from 'components/NoEventsMask';
import HorizontalLoader from 'components/HorizontalLoader';
import WidgetOnMobile from 'scenes/Sidebar/components/WidgetOnMobile';
import MediaQuery from 'react-responsive';
import FiltersMobileToggler from 'scenes/Sidebar/components/FiltersMobileToggler';

import withOneTimePopup from 'hocs/withOneTimePopup';
import { useTokenAction } from 'hooks/useTokenAction';
import { useGrid } from 'hooks/useGrid';
import { updateHistory } from 'helpers/grid-history';
import { useGridFilters } from 'hooks/useGridFilters';
import { Permission, Role } from 'helpers/user';
import { ENDPOINT } from 'constants/endpoints';
import { UPGRADE_ACCOUNT_TYPE } from 'constants/upgrade-account-types';

import { CSSTransition } from 'react-transition-group';
import { ANIMATION_TIMEOUT_APPEAR } from 'constants/animation.js';

import './styles.css';

const Network = props => {
  const { user, history } = props;

  const filters = useGridFilters(ENDPOINT.FILTERS.NETWORK);

  const grid = useGrid(ENDPOINT.COLLECTION.NETWORK, params => {
    updateHistory(history, params);
  });

  useTokenAction(history);

  const [mobileFilters, toggleMobileFilters] = useState(false);

  const toggleMobileFiltersHandler = () => {
    toggleMobileFilters(!mobileFilters);
  };

  const sortControls = [
    {
      defaultCaption: 'All',
      toggledCaption: 'All',
      value: { opt: 0 },
    },
    {
      defaultCaption: 'Firm Alumni',
      toggledCaption: 'Firm Alumni',
      value: { opt: 2 },
    },
    {
      defaultCaption: 'School Alumni',
      toggledCaption: 'School Alumni',
      value: { opt: 3 },
    },
  ];

  const [isRecruiter, isRecruiterPremium, isAllowedUseNetworkFilters] = useMemo(
    () => [
      Role.isRecruiter(user),
      Role.isRecruiterPremium(user),
      Permission.isAllowedUseNetworkFilters(user.permissions),
    ],
    [user]
  );

  const handleUpgradeAcc = e => {
    if (!isAllowedUseNetworkFilters) {
      e.preventDefault();
      if (UPGRADE_ACCOUNT_TYPE.RECRUITER === user.accountUpgradeType) {
        UpgradeRecruiterAccount();
      } else {
        UpgradeCandidateAccount({ history });
      }
    }
  };

  const filtersAccordion = useMemo(
    () =>
      filters.data && (
        <FiltersAccordion
          title={'Search Network'}
          subTitle={
            'Use the search filters below to narrow your network search.'
          }
          disabled={!isAllowedUseNetworkFilters}
          filters={filters.data}
          onClickHandler={handleUpgradeAcc}
          onChange={filters.actions.change}
          onGroupSelectAll={filters.actions.groupSelect}
          onGroupClearAll={filters.actions.groupClear}
          onClearAll={filters.actions.clear}
          onSearch={filters.actions.search}
          onPushValue={filters.actions.push}
          render={renderProps => <FiltersWidget {...renderProps} />}
        />
      ),
    [filters.data, isAllowedUseNetworkFilters, isRecruiter, isRecruiterPremium]
  );

  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    const action = params.get('action');

    let mounted = true;
    if (user.popups.network && !action) {
      props.oneTimePopup.open(user.popups.network, () =>
        mounted && grid.isFetching ? grid.actions.fetch() : null
      );
    }

    return () => {
      mounted = false;
    };
  }, []);

  const onCandidateStatusChange = (id, status, data = {}) => {
    grid.actions.setDataRows(
      grid.data.rows.map(item =>
        item.CandidateID === id
          ? {
              ...item,
              ConnectStatus: status,
              ...data,
            }
          : item
      )
    );
  };

  return (
    <GridHistoryWithFilters
      history={history}
      grid={grid}
      filters={filters}
      initialParams={{ opt: 0 }}
    >
      <PageLayout
        pageClass="network-page"
        pageTitle="Network"
        mainContent={
          <>
            {grid.data.init ? (
              <ListSearch
                search={grid.data.params.search || false}
                sortBy={
                  grid.data.params.opt
                    ? { opt: grid.data.params.opt }
                    : { opt: 0 }
                }
                sortControls={sortControls}
                searchPlaceholder="Search network"
                onChangeSortByHandler={grid.actions.sortBy}
                onSendSearchQueryHandler={grid.actions.search}
                renderHeader={
                  <MediaQuery query="(max-width: 767px)">
                    <FiltersMobileToggler
                      title="Search Filters:"
                      toggleFunc={toggleMobileFiltersHandler}
                    />
                  </MediaQuery>
                }
              />
            ) : (
              <ListSearchContentLoader />
            )}
            <div className="horizontal-loader__container">
              {grid.data.isFetching && <HorizontalLoader />}
            </div>
            <List
              items={grid.data.rows}
              itemRender={(item, actions) => (
                <NetworkItem
                  user={user}
                  key={item.CandidateID}
                  item={item}
                  actions={actions}
                />
              )}
              actions={{
                onStatusChangeHandler: onCandidateStatusChange,
                onChangePage: grid.actions.readPage,
              }}
              pagination={{
                page: grid.data.page,
                count: grid.data.rowsCount,
                perPage: grid.data.perPage,
              }}
            />
          </>
        }
        sidebarContent={
          grid.data.init ? (
            <Sidebar>
              {grid.data.isFetching && (
                <CSSTransition
                  in={grid.data.init}
                  appear={true}
                  timeout={ANIMATION_TIMEOUT_APPEAR}
                  classNames="fade"
                >
                  <NoEventsMask relative={true} />
                </CSSTransition>
              )}
              <WidgetOnMobile
                renderComponent={filtersAccordion}
                status={mobileFilters}
                toggleFunc={toggleMobileFiltersHandler}
              />
            </Sidebar>
          ) : (
            <FiltersContentLoader />
          )
        }
      />
    </GridHistoryWithFilters>
  );
};

export default compose(withOneTimePopup)(Network);
